import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Refresh from '@material-ui/icons/Refresh';
import PropTypes from 'prop-types';
import React from 'react';

const styles = (theme) => ({
  widgetWrapper: {
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  widgetHeader: {
    padding: theme.spacing.unit * 2,
    paddingBottom: theme.spacing.unit,
  },
  widgetTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  widgetDescription: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing.unit,
  },
  widgetContainer: {
    flex: 1,
    position: 'relative',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: 'transparent',
  },
});

class _WidgetContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      iframeHeight: 900, // Or a default min-height like 200
      scrolledFrom: null,
      isRefreshingAuth: false,
      hasAuthError: false, // Track if widget has auth error
    };
    this.paperRef = React.createRef();
    this.iframeRef = React.createRef();
    this.refreshWidgetAuth = this.refreshWidgetAuth.bind(this);
  }
  componentDidMount() {
    window.addEventListener('message', this.handleIframeMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleIframeMessage);
  }

  findScrollableContainer = (element) => {
    if (!element) return null;

    // Check if the current element is scrollable
    const style = window.getComputedStyle(element);
    const isScrollable =
      style.overflow === 'auto' ||
      style.overflow === 'scroll' ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll';

    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }

    // Check parent elements
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      const parentStyle = window.getComputedStyle(parent);
      const parentIsScrollable =
        parentStyle.overflow === 'auto' ||
        parentStyle.overflow === 'scroll' ||
        parentStyle.overflowY === 'auto' ||
        parentStyle.overflowY === 'scroll';

      if (parentIsScrollable && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  };

  handleIframeMessage = (event) => {
    // IMPORTANT: Verify the origin of the message for security
    // const expectedOrigin = new URL(process.env.REACT_APP_NEW_UV_URL).origin;
    // if (event.origin !== expectedOrigin) {
    //   console.warn('Message from unexpected origin:', event.origin);
    //   return;
    // }

    if (
      event.data &&
      event.data.type === 'iframeResize' &&
      typeof event.data.height === 'number' &&
      this.props.autoFullHeight !== true
    ) {
      this.setState({ iframeHeight: event.data.height });
    }

    // Handle custom widget events
    if (
      event.data &&
      event.data.type === 'widget_event' &&
      this.props.onWidgetEvent &&
      typeof this.props.onWidgetEvent === 'function'
    ) {
      this.props.onWidgetEvent(
        event.data.widgetName,
        event.data.eventType,
        event.data.eventData
      );
    }

    if (event.data && event.data.type === 'dialog_toggle') {
      const { open } = event.data;

      if (open) {
        let scrollElement = null;
        let currentScrollTop = 0;

        // Try to get the actual DOM element
        const paperElement = this.paperRef.current;

        if (paperElement) {
          // Find the scrollable container starting from the paper element
          scrollElement = this.findScrollableContainer(paperElement);

          if (scrollElement) {
            currentScrollTop = scrollElement.scrollTop;
            console.log('Container scrollTop:', currentScrollTop);
          }
        }

        // If no scrollable container found, use window
        if (!scrollElement) {
          currentScrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          scrollElement = window;
          console.log(
            'Using window as scroll element, scrollTop:',
            currentScrollTop
          );
        }

        // Store the current scroll position
        this.setState({
          scrolledFrom: currentScrollTop,
        });

        // Scroll to top
        if (scrollElement === window) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else if (scrollElement) {
          if (typeof scrollElement.scrollTo === 'function') {
            scrollElement.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          } else {
            scrollElement.scrollTop = 0;
          }
        }

        console.log('Scrolled to top, stored position:', currentScrollTop);
      } else {
        // Restore the previous scroll position
        const storedPosition = this.state.scrolledFrom;
        console.log('Restoring scroll position:', storedPosition);

        if (storedPosition !== null) {
          const paperElement = this.paperRef.current;
          let scrollElement = null;

          if (paperElement) {
            scrollElement = this.findScrollableContainer(paperElement);
          }

          if (scrollElement) {
            if (typeof scrollElement.scrollTo === 'function') {
              scrollElement.scrollTo({
                top: storedPosition,
                behavior: 'smooth',
              });
            } else {
              scrollElement.scrollTop = storedPosition;
            }
            console.log('Restored container scroll to:', storedPosition);
          } else {
            // Use window
            window.scrollTo(0, storedPosition, { behavior: 'smooth' });
            console.log('Restored window scroll to:', storedPosition);
          }
        }

        this.setState({
          scrolledFrom: null,
        });
      }
    }

    // Handle widget authentication refresh request
    if (event.data && event.data.type === 'widget_auth_request') {
      console.log('Received widget_auth_request');
      this.setState({ hasAuthError: true });
      this.refreshWidgetAuth();
    }
  };

  getWidgetUrl = () => {
    const { widgetName, widgetProps = {} } = this.props;

    const auth = window.localStorage.getItem(
      process.env.REACT_APP_SA_CACHE_KEY
    );

    if (!auth) {
      throw new Error('No auth');
    }

    const pAuth = JSON.parse(auth);

    const encodedProps = encodeURIComponent(JSON.stringify(widgetProps));

    const baseUrl = process.env.REACT_APP_NEW_UV_URL;
    const authParams = `?accessToken=${pAuth.access_token}&refreshToken=${pAuth.refresh_token}&widget=${widgetName}&widgetProps=${encodedProps}`;
    const url = `${baseUrl}/sso${authParams}`;

    return url;
  };

  refreshWidgetAuth = () => {
    // Prevent duplicate refresh attempts
    if (this.state.isRefreshingAuth) {
      console.log('Already refreshing auth, skipping...');
      return;
    }

    this.setState({ isRefreshingAuth: true });

    try {
      console.log('Refreshing widget authentication');

      // Get fresh tokens from UV1's localStorage
      const auth = window.localStorage.getItem(
        process.env.REACT_APP_SA_CACHE_KEY
      );

      if (!auth) {
        console.error('No auth tokens in UV1 localStorage');
        // Show error message if snackbar available
        if (this.props.open_snack_ac) {
          this.props.open_snack_ac({
            variant: 'error',
            message: 'Your session has expired. Please refresh the page and log in again.'
          });
        }
        this.setState({ isRefreshingAuth: false });
        return;
      }

      // Reconstruct the widget URL with fresh tokens
      const newUrl = this.getWidgetUrl();
      console.log('New widget URL generated');

      // Reload iframe with new authentication
      if (this.iframeRef.current) {
        this.iframeRef.current.src = newUrl;
        console.log('Iframe src updated, authentication refreshed');
      }

      // Reset state after iframe loads - clear auth error flag
      setTimeout(() => {
        this.setState({ isRefreshingAuth: false, hasAuthError: false });
      }, 2000);
    } catch (error) {
      console.error('Error refreshing widget auth:', error);
      this.setState({ isRefreshingAuth: false });
    }
  };

  render() {
    const {
      classes,
      widgetName,
      title,
      description,
      className = '',
      style = {},
    } = this.props;
    const { iframeHeight, hasAuthError } = this.state;

    return (
      <div ref={this.paperRef}>
        <Paper
          className={`${classes.widgetWrapper} ${className}`}
          style={style}
          elevation={1}>
          {/* Optional header for title/description */}
          {(title || description) && (
            <div className={classes.widgetHeader}>
              {title && (
                <Typography className={classes.widgetTitle} variant="subtitle1">
                  {title}
                </Typography>
              )}
              {description && (
                <Typography
                  className={classes.widgetDescription}
                  variant="body2">
                  {description}
                </Typography>
              )}
            </div>
          )}

          <div className={classes.widgetContainer}>
            {hasAuthError ? (
              // Show auth error message instead of iframe
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: '300px',
                padding: '40px 20px',
                textAlign: 'center',
              }}>
                <Refresh style={{ fontSize: 64, color: '#f59e0b', marginBottom: 16 }} />
                <Typography variant="h6" style={{ marginBottom: 8, color: '#1f2937' }}>
                  Session Expired
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 24, color: '#6b7280', maxWidth: 400 }}>
                  Your session has expired. Click the button below to refresh your authentication and continue.
                </Typography>
                <Button
                  onClick={this.refreshWidgetAuth}
                  variant="contained"
                  disabled={this.state.isRefreshingAuth}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                  }}>
                  <Refresh style={{ marginRight: 8, fontSize: 20 }} />
                  {this.state.isRefreshingAuth ? 'Refreshing...' : 'Refresh Authentication'}
                </Button>
              </div>
            ) : (
              // Show normal iframe
              <iframe
                src={this.getWidgetUrl()}
                ref={this.iframeRef}
                className={classes.iframe}
                style={{
                  height: this.props.autoFullHeight
                    ? 'calc(100vh - 150px)'
                    : iframeHeight
                    ? `${iframeHeight}px`
                    : 'auto',
                }}
                title={`${widgetName} Widget`}
              />
            )}
          </div>
        </Paper>
      </div>
    );
  }
}

_WidgetContainer.propTypes = {
  classes: PropTypes.object.isRequired,
  widgetName: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  widgetProps: PropTypes.object,
  onWidgetEvent: PropTypes.func,
};

export const WidgetContainer = withStyles(styles)(_WidgetContainer);
